import * as React from 'react';
import { useForm } from 'react-hook-form';

import {
  Alert,
  Heading,
  Select,
  TextField,
  Button,
  Row,
  Col,
} from '@elevationscu/ui-component-lib';
import styles from '../App.module.scss';
import ProgressBar from './ProgressBar';
import InputRequest from './input-request';
import DataDisclaimer from './data-disclaimer';
import prospectPut, { formatPhoneNumber } from './helper-functions';
import { EMAIL_REGEX, PHONE_REGEX } from './constants';

export default function Page1({
  pageNumber, userInfo, setUserInfo, nextPage, maxPages,
}) {
  const {
    register, handleSubmit, reset, trigger, formState: { errors, isValid },
  } = useForm({ mode: 'all' });

  // eslint-disable-next-line no-unused-vars
  const [isReseted, setIsReseted] = React.useState(false);

  const [businessName, setBusinessName] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [depositedPhone, setDepositedPhone] = React.useState(phone);
  const [isMember, setIsMember] = React.useState('');
  const [methodOfContact, setMethodOfContact] = React.useState('No Preference');
  const [duplicateEmailError, setDuplicateEmailError] = React.useState(null);

  const validationSchema = {
    businessName: {
      required: true,
      message: 'Business name is required',
      maxLength: 50,
    },
    firstName: {
      required: true,
      message: 'First name is required',
      maxLength: 50,
    },
    lastName: {
      required: true,
      message: 'Last name is required',
      maxLength: 50,
    },
    email: {
      required: true,
      message: 'Valid email is required',
      maxLength: 100,
      pattern: EMAIL_REGEX,
    },
    phone: {
      required: true,
      message: 'Phone number must be 10 digits',
      maxLength: 14,
      minLength: 10,
      pattern: PHONE_REGEX,
    },
    isMember: {
      required: true,
      message: 'You must select Yes or No',
      maxLength: 3,
      minLength: 2,
    },
    methodOfContact: {
      required: true,
      message: 'You must select Email or Phone',
      validate: (value) => value === 'Phone' || value === 'Email' || value === 'No Preference',
    },
  };

  React.useEffect(() => {
    if (userInfo.businessName) {
      setBusinessName(userInfo.businessName);
    }
    if (userInfo.lastName) setLastName(userInfo.lastName);
    if (userInfo.firstName) setFirstName(userInfo.firstName);
    if (userInfo.email) setEmail(userInfo.email);
    if (userInfo.phone) setPhone(formatPhoneNumber(userInfo.phone));
    if (userInfo.phone) setDepositedPhone(userInfo.phone);
    if (userInfo.isMember) setIsMember(userInfo.isMember);
    if (userInfo.methodOfContact) setMethodOfContact(userInfo.methodOfContact);

    // eslint-disable-next-line
    }, [])

  async function next() {
    trigger();

    setUserInfo(
      Object.assign(userInfo, {
        businessName,
        lastName,
        firstName,
        email,
        phone: depositedPhone,
        isMember,
        methodOfContact,
        // application_data: {},
        app_status: 'Incomplete',
      }),
    );

    const response = await prospectPut(userInfo);
    if (response === 400) {
      // console.log('something went wrong');
      setDuplicateEmailError("We're sorry for the inconvenience, but we're unable to process your request. Please call 800.460.1620 for immediate assistance.");
      return;
    }
    nextPage(userInfo);
  }

  function handleInput(e) {
    const formattedPhoneNumber = formatPhoneNumber(e.target.value).slice(0, 14);
    const integerOnlyFormat = formattedPhoneNumber.replace(/\D/g, '');
    // phone number displayed to the user, formatted as (xxx) xxx-xxxx
    setPhone(formattedPhoneNumber);
    // number desposited to the DB, stripped of all non integer characters
    if (integerOnlyFormat === '') {
      setDepositedPhone(null);
    } else {
      setDepositedPhone(integerOnlyFormat);
    }
  }

  React.useEffect(() => {
    // initial loading is done, now can reset validation
    reset();
    setIsReseted(true);
  }, [reset]);

  React.useEffect(() => {
    // just need to trigger one after resetting validation.
    if (businessName) trigger('businessName');
  }, [businessName, trigger]);

  return (
    <div>
      <Row>
        <Col>
          <Heading size={2} element="h1" className={styles.dataFormHeader}>
            First, let’s learn a little about you
          </Heading>
        </Col>
      </Row>
      <Row>
        <Col>
          <InputRequest firstPage />
        </Col>
      </Row>
      {/* </div> */}
      <div className={styles.prospectInput}>
        <Row>
          <Col>
            <ProgressBar pageNumber={pageNumber} maxPages={maxPages} />
          </Col>
        </Row>
        <Row>
          <Col>
            <Heading size={3} element="h2" className={styles.inputHeader}>
              Your information
            </Heading>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className={styles.dataFormSpan}>
              Please provide your contact information so we can contact you to discuss your rate proposal.
            </div>
          </Col>
        </Row>
        <div className={styles.dataInputs}>
          <Row>
            <Col xs={12} lg={4}>
              <div className={styles.indivInput}>
                <TextField
                  type="text"
                  label="Business name (required)"
                  id="inputBusinessName"
                  value={businessName}
                  error={errors.businessName && validationSchema.businessName.message}
                  {...register('businessName', { required: true, onChange: (e) => { setBusinessName(e.target.value); } })}
                />
              </div>
            </Col>
            <Col xs={12} lg={4}>
              <div className={styles.indivInput}>
                <TextField
                  type="text"
                  label="First name (required)"
                  id="inputFirstName"
                  value={firstName}
                  error={errors.firstName && validationSchema.firstName.message}
                  {...register('firstName', { required: true, onChange: (e) => setFirstName(e.target.value) })}
                />
              </div>
            </Col>
            <Col xs={12} lg={4}>
              <div className={styles.indivInput}>
                <TextField
                  type="text"
                  label="Last name (required)"
                  id="inputLastName"
                  value={lastName}
                  error={errors.lastName && validationSchema.lastName.message}
                  {...register('lastName', { required: true, onChange: (e) => setLastName(e.target.value) })}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} lg={4}>
              <div className={styles.indivInput}>
                <TextField
                  type="text"
                  label="Email (required)"
                  id="inputBusinessEmail"
                  value={email}
                  error={errors.email && validationSchema.email.message}
                  {...register('email', { ...validationSchema.email, onChange: (e) => { setEmail(e.target.value); } })}
                />
              </div>
            </Col>
            <Col xs={12} lg={4}>
              <div className={styles.indivInput}>
                <TextField
                  label="Phone number (required)"
                  data-testid="testPhoneNumber"
                  id="inputPhoneNumber"
                  value={phone}
                  error={errors.phone && validationSchema.phone.message}
                  {...register('phone', { ...validationSchema.phone, onChange: (e) => handleInput(e) })}
                />
              </div>
            </Col>
            <Col xs={12} lg={4}>
              <div className={styles.indivInput}>
                <Select
                  name="isMember"
                  id="isMember"
                  value={isMember}
                  label="Are you an Elevations member? (required)"
                  style={{ paddingLeft: '5px' }}
                  error={errors.isMember && validationSchema.isMember.message}
                  {...register('isMember', { ...validationSchema.isMember, onChange: (e) => { setIsMember(e.target.value); } })}
                >
                  <option value="" disabled hidden>{' '}</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </Select>
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} lg={4}>
              <div className={styles.indivInput}>
                <Select
                  name="methodOfContact"
                  id="methodOfContact"
                  value={methodOfContact}
                  label="Preferred method of contact (required)"
                  style={{ paddingLeft: '5px' }}
                  error={errors.methodOfContact && validationSchema.methodOfContact.message}
                  {...register('methodOfContact', { ...validationSchema.methodOfContact, onChange: (e) => { setMethodOfContact(e.target.value); } })}
                >
                  <option value="No Preference">No Preference</option>
                  <option value="Phone">Phone</option>
                  <option value="Email">Email</option>
                </Select>
              </div>
            </Col>
          </Row>
        </div>
        <div className={styles.loanOfficerOptions}>
          <Row>
            <Col lg={6}>
              <DataDisclaimer firstPage />
            </Col>
            <Col>
              {duplicateEmailError
                && (
                <div>
                  <Alert variant="error">
                    {duplicateEmailError}
                  </Alert>
                </div>
                )}
              <div className={styles.nextButton}>
                <Button
                  disabled={!isValid}
                  onClick={handleSubmit(() => {
                    next();
                  })}
                  id="dataNextButton"
                >
                  Next
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}
